/**
 * Platform abstraction layer for React Native compatibility.
 *
 * This module provides interfaces and default (web) implementations for
 * browser-specific APIs that are unavailable in React Native:
 *   - Storage        (localStorage / sessionStorage)
 *   - TextEncoder
 *   - Base-64        (btoa / atob)
 *   - SubtleCrypto   (crypto.subtle.digest)
 *   - DOM helpers    (document.*, requestAnimationFrame)
 *   - Location       (window.location.origin)
 *   - UserAgent      (navigator.userAgent)
 *
 * React Native consumers call `setPlatform(adapter)` once at startup to
 * supply their own implementations (e.g. AsyncStorage-backed storage,
 * expo-crypto, react-native-get-random-values, etc.).
 *
 * Web consumers don't need to do anything — the defaults use the browser APIs.
 */
var _a;
/**
 * DS3-0416: brand for the volatile in-memory fallback so callers whose
 * correctness depends on durability (idempotency keys that must survive a reload
 * / RN restart) can detect that no durable store is present and fail closed
 * instead of silently persisting to memory that vanishes on restart.
 */
const VOLATILE_STORAGE = Symbol.for('bounded.volatileStorage');
/** In-memory fallback when neither browser storage nor a custom adapter exist. */
class MemoryStorage {
    constructor() {
        this[_a] = true;
        this.store = new Map();
    }
    getItem(key) { var _b; return (_b = this.store.get(key)) !== null && _b !== void 0 ? _b : null; }
    setItem(key, value) { this.store.set(key, value); }
    removeItem(key) { this.store.delete(key); }
}
_a = VOLATILE_STORAGE;
/**
 * DS3-0416: true when the adapter is the volatile in-memory fallback (no durable
 * browser storage and no custom platform adapter). A branded value rather than
 * an `instanceof` so it survives bundling and works for any future volatile
 * fallback that opts in.
 */
export function isVolatileStorage(store) {
    return !!(store === null || store === void 0 ? void 0 : store[VOLATILE_STORAGE]);
}
// ---------------------------------------------------------------------------
// Default web implementation
// ---------------------------------------------------------------------------
function createWebPlatform() {
    const hasWindow = typeof window !== 'undefined';
    const hasDoc = typeof document !== 'undefined';
    return {
        storage: hasWindow && typeof localStorage !== 'undefined'
            ? localStorage
            : new MemoryStorage(),
        sessionStorage: hasWindow && typeof sessionStorage !== 'undefined'
            ? sessionStorage
            : new MemoryStorage(),
        textEncode(input) {
            return new TextEncoder().encode(input);
        },
        atob(input) {
            if (hasWindow)
                return window.atob(input);
            // Node / SSR fallback
            return Buffer.from(input, 'base64').toString('binary');
        },
        btoa(input) {
            if (hasWindow)
                return window.btoa(input);
            return Buffer.from(input, 'binary').toString('base64');
        },
        async sha256(data) {
            if (typeof crypto !== 'undefined' && crypto.subtle) {
                return crypto.subtle.digest('SHA-256', data);
            }
            // Node / SSR fallback. Keep the specifier indirect so browser
            // bundlers do not externalize or preload a Node-only module in the
            // email/guest graph; this branch is unreachable when WebCrypto is
            // available (all supported browsers and current Node releases).
            const nodeCryptoSpecifier = 'node:crypto';
            const { createHash } = await import(/* @vite-ignore */ nodeCryptoSpecifier);
            const hash = createHash('sha256').update(data).digest();
            return hash.buffer.slice(hash.byteOffset, hash.byteOffset + hash.byteLength);
        },
        getRandomBytes(length) {
            const out = new Uint8Array(length);
            const g = typeof globalThis !== 'undefined' ? globalThis : undefined;
            if (g && g.crypto && typeof g.crypto.getRandomValues === 'function') {
                g.crypto.getRandomValues(out);
                return out;
            }
            throw new Error('No secure random source available for PKCE. On React Native, either import ' +
                "'react-native-get-random-values' once at your app entry, or pass getRandomBytes " +
                'via setPlatform({ getRandomBytes }) (e.g. expo-crypto getRandomBytes).');
        },
        getUserAgent() {
            if (hasWindow && typeof navigator !== 'undefined')
                return navigator.userAgent;
            return '';
        },
        getLocationOrigin() {
            if (hasWindow && window.location)
                return window.location.origin;
            return undefined;
        },
        hasDOM: hasDoc,
    };
}
// ---------------------------------------------------------------------------
// Singleton & public API
// ---------------------------------------------------------------------------
let _platform = createWebPlatform();
let _platformConfigured = false;
/**
 * Override the platform adapter (call once at app startup in React Native).
 *
 * Must be called **before** `init()`. Can only be called once — subsequent
 * calls throw to prevent accidental mid-session storage swaps.
 *
 * ```ts
 * import { setPlatform } from '@bounded-sh/client';
 * import AsyncStorage from '@react-native-async-storage/async-storage';
 *
 * // Create a synchronous wrapper around AsyncStorage for the adapter
 * // (see docs for a full example).
 * setPlatform({ storage: myStorage, ... });
 * ```
 */
export function setPlatform(adapter) {
    if (_platformConfigured) {
        throw new Error('setPlatform() has already been called. It can only be invoked once at startup.');
    }
    _platformConfigured = true;
    _platform = Object.assign(Object.assign({}, _platform), adapter);
}
/** Get the current platform adapter. */
export function getPlatform() {
    return _platform;
}
// ---------------------------------------------------------------------------
// Mobile / Android detection helpers
// ---------------------------------------------------------------------------
/**
 * Detect mobile device even when Chrome "Request Desktop Site" is active.
 *
 * Desktop mode on Android strips "Android" and "Mobile" from the UA, leaving
 * something like "Linux x86_64 …". The fallback detects this by looking for
 * touch capability + small viewport + a UA that doesn't belong to a known
 * desktop OS (ChromeOS, Windows, macOS).
 */
export function detectMobile() {
    const ua = _platform.getUserAgent();
    if (/Android|iPhone|iPad|iPod/i.test(ua))
        return true;
    if (typeof navigator !== 'undefined' && typeof window !== 'undefined') {
        const hasTouch = navigator.maxTouchPoints > 0;
        // Macs don't have touch screens, so "Macintosh" + touch = iOS desktop mode
        // (also catches iPadOS 13+ which always reports as Macintosh)
        if (/Macintosh/i.test(ua) && hasTouch)
            return true;
        // Android desktop mode: Linux UA + touch + small viewport, excluding
        // known desktop OSes (ChromeOS, Windows, macOS)
        const isSmallViewport = window.innerWidth <= 1024;
        const isDesktopOS = /CrOS|Windows|Macintosh/i.test(ua);
        if (hasTouch && isSmallViewport && !isDesktopOS)
            return true;
    }
    return false;
}
/**
 * Detect Android, including desktop-mode Chrome and Seeker/Saga in-app browsers.
 */
export function detectAndroid() {
    const ua = _platform.getUserAgent();
    if (/Android/i.test(ua))
        return true;
    if (/SolanaWallet|SeedVault/i.test(ua))
        return true;
    // Desktop-mode fallback: detected as mobile + Linux UA + not another OS
    if (detectMobile() && /Linux/i.test(ua) && !/CrOS|Macintosh|iPhone|iPad/i.test(ua))
        return true;
    return false;
}
/**
 * Reset the platform to web defaults and clear the configured flag.
 * **For testing only** — not exported from the public API.
 * @internal
 */
export function _resetPlatformForTesting() {
    _platform = createWebPlatform();
    _platformConfigured = false;
}
